import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGetData,
  type GetArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGetError,
  type GetArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGetResponse,
  getArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGet,
} from '../../generated';

export type GetArtifactQueryVariables = Omit<
  GetArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGetData['path'],
  'portfolio_id'
> & {
  workspace_id: string;
  organization_id: string;
};

export type GetArtifactQuery = GetArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGetResponse;

export const useGetArtifactQuery = <
  TData = GetArtifactQuery,
  TError = GetArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGetError,
>(
  { organization_id, workspace_id, image_id, artifact_type }: GetArtifactQueryVariables,
  options?: Omit<UseQueryOptions<GetArtifactQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetArtifactQuery, TError, TData>({
    queryKey: ['GetArtifact', { organization_id, workspace_id, image_id, artifact_type }],
    queryFn: async () => {
      const { data } = await getArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGet({
        path: { workspace_id, image_id, artifact_type },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetArtifactQuery.getKey = ({
  organization_id,
  workspace_id,
  image_id,
  artifact_type,
}: GetArtifactQueryVariables) => ['GetArtifact', { organization_id, workspace_id, image_id, artifact_type }];

useGetArtifactQuery.fetcher = (
  { organization_id: _organization_id, workspace_id, image_id, artifact_type }: GetArtifactQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getArtifactV1WorkspacesWorkspaceIdAssetsImagesImageIdArtifactsArtifactTypeGet({
      path: { workspace_id, image_id, artifact_type },
      throwOnError: true,
    });
    return data;
  };
};
